import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Table,
  Card,
  Icon,
  Segment,
  Message,
  Dimmer,
  Loader
} from "semantic-ui-react";
import { useToast } from "@chakra-ui/react";
import axios from "axios";
import URL from "../config/config.json";

const baseUrl = URL.baseUrl;

function CertificateVerifyModal({
  isCertificateVerifyModalOpen,
  setIsCertificateVerifyModalOpen,
  testId,
}) {
  const toast = useToast();
  
  // State for Certificate Verification
  const [certificateId, setCertificateId] = useState("");
  const [certificateData, setCertificateData] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState(null);
  const [hintToastShown, setHintToastShown] = useState(false);

  useEffect(() => {
    if (isCertificateVerifyModalOpen) {
      // Reset all data on modal open
      resetAllData();
    }
  }, [isCertificateVerifyModalOpen]);

  const resetAllData = () => {
    setCertificateData(null);
    setCertificateError(null);
    setCertificateId("");
    setHintToastShown(false);
  };

  const handleCertificateVerify = async () => {
    if (!certificateId.trim()) {
      setCertificateError("Please enter a Certificate ID");
      return;
    }
    
    setCertificateLoading(true);
    setCertificateError(null);
    
    try {
      const response = await axios.get(`${baseUrl}/fetch_certificate_details/${certificateId}/`);
      setCertificateData(response.data);
    } catch (error) {
      setCertificateError("Certificate not found or invalid ID. Please check and try again.");
      setCertificateData(null);
    } finally {
      setCertificateLoading(false);
    }
  };

  const showHintToast = () => {
    if (!hintToastShown) {
      toast({
        title: "Sample Certificate ID: 1708#####1_3##0",
        status: "info",
        position: "top",
        isClosable: true,
        duration: 4000,
      });
      setHintToastShown(true);
    }
  };

  return (
    <Modal
      closeIcon
      open={isCertificateVerifyModalOpen}
      onClose={() => setIsCertificateVerifyModalOpen(false)}
      size="large"
      style={{ height: 'auto' }}
    >
      <Modal.Header style={{ 
        backgroundColor: '#f8f9fa', 
        borderBottom: '2px solid #e9ecef',
        padding: '20px 30px'
      }}>
        <Icon name="certificate" color="blue" />
        Certificate Verification Portal
      </Modal.Header>

      <Modal.Content style={{ padding: '30px' }}>
        <Segment style={{ minHeight: '500px', border: 'none', boxShadow: 'none' }}>
          <div style={{ textAlign: 'center', marginBottom: '30px' }}>
            <Icon name="certificate" size="huge" color="blue" />
            <h2 style={{ color: '#2185d0', marginTop: '10px' }}>Certificate Verification</h2>
            <p style={{ color: '#666', fontSize: '16px' }}>
              Enter your certificate ID to verify its authenticity
            </p>
          </div>

          <Card fluid style={{ padding: '20px' }}>
            <Form>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600',
                  color: '#333'
                }}>
                  Certificate ID *
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Input
                    type="text"
                    placeholder="Enter your Certificate ID"
                    value={certificateId}
                    onChange={(e) => setCertificateId(e.target.value)}
                    style={{ flex: 1 }}
                    size="large"
                    onKeyPress={(e) => e.key === 'Enter' && handleCertificateVerify()}
                  />
                  <span 
                    onClick={showHintToast} 
                    style={{ 
                      cursor: "pointer",
                      color: "#2185d0",
                      fontSize: '14px',
                      textDecoration: 'underline'
                    }}
                  >
                    Need help? View sample ID
                  </span>
                </div>
              </div>

              <Button
                color="blue"
                onClick={handleCertificateVerify}
                loading={certificateLoading}
                disabled={certificateLoading || !certificateId.trim()}
                size="large"
                style={{ 
                  width: '200px',
                  display: 'block',
                  margin: '20px auto'
                }}
              >
                <Icon name="search" />
                Verify Certificate
              </Button>
            </Form>

            {certificateError && (
              <Message negative style={{ marginTop: '20px' }}>
                <Icon name="exclamation triangle" />
                {certificateError}
              </Message>
            )}

            {certificateData && (
              <div style={{ marginTop: '30px' }}>
                <Message positive>
                  <Icon name="check circle" />
                  Certificate verified successfully!
                </Message>
                
                <Table celled striped style={{ marginTop: '20px' }}>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell style={{ backgroundColor: '#f8f9fa' }}>Field</Table.HeaderCell>
                      <Table.HeaderCell style={{ backgroundColor: '#f8f9fa' }}>Details</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <Table.Row>
                      <Table.Cell><strong>Name</strong></Table.Cell>
                      <Table.Cell>{certificateData.username}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Company</strong></Table.Cell>
                      <Table.Cell>{certificateData.company_name}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Course</strong></Table.Cell>
                      <Table.Cell>{certificateData.course_name}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Issuer Designation</strong></Table.Cell>
                      <Table.Cell>{certificateData.designation}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Issuer Name</strong></Table.Cell>
                      <Table.Cell>{certificateData.issuer_name}</Table.Cell>
                    </Table.Row>
                    <Table.Row>
                      <Table.Cell><strong>Exam Date</strong></Table.Cell>
                      <Table.Cell>{certificateData.exam_date}</Table.Cell>
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>
            )}
          </Card>
        </Segment>
      </Modal.Content>

      <Modal.Actions style={{ 
        backgroundColor: '#f8f9fa', 
        borderTop: '1px solid #e9ecef',
        padding: '15px 30px'
      }}>
        <Button 
          onClick={() => setIsCertificateVerifyModalOpen(false)}
          size="large"
        >
          <Icon name="close" />
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default CertificateVerifyModal;